import React, { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';

import Input from 'components/Inputs/Input';
import Select from 'components/Select';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import { getMessageForInvalidFields, isValidEmail, validatePhoneNumber } from 'utils/validationUtils';
import { getErrorMessageForRequiredFields } from 'utils/stringUtils';
import { InputLabel } from 'pages/ContractorPortal/steps/ContractorApplication/contractorApplication.enums';
import { MAIN_CONTACT_POSITION_OPTIONS } from 'pages/ContractorPortal/steps/ContractorApplication/сontractorApplication.constants';
import styles from 'pages/ContractorPortal/steps/ContractorApplication/ContractorApplication.module.scss';

const ContactPersonInformation = () => {
  const {
    register,
    unregister,
    setValue,
    control,
    watch,
    trigger,
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const watcher = watch();

  useEffect(() => {
    register('intermediary_main_contact_first_name', {
      required: {
        value: !watcher.intermediary_main_contact_first_name,
        message: getErrorMessageForRequiredFields(InputLabel.BusinessOwnerFirstName),
      },
    });
    register('intermediary_main_contact_last_name', {
      required: {
        value: !watcher.intermediary_main_contact_last_name,
        message: getErrorMessageForRequiredFields(InputLabel.BusinessOwnerLastName),
      },
    });
    register('intermediary_main_contact_phone', {
      required: {
        value: !watcher.intermediary_main_contact_phone,
        message: getErrorMessageForRequiredFields(InputLabel.PrimaryContactPersonPhone),
      },
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register('intermediary_main_contact_email', {
      required: {
        value: !watcher.intermediary_main_contact_email,
        message: getErrorMessageForRequiredFields(InputLabel.PrimaryContactPersonEmail),
      },
      validate: (value) => {
        return isValidEmail(value) || getMessageForInvalidFields(InputLabel.PrimaryContactPersonEmail);
      },
    });
    register('intermediary_main_contact_position', {
      required: {
        value: !watcher.intermediary_main_contact_position,
        message: getErrorMessageForRequiredFields(InputLabel.PrimaryContactPersonTitle),
      },
    });
  }, [register, unregister, watcher]);

  return (
    <>
      <div className={styles.blockHeader}>Contact Person Information</div>
      <div className={styles.subTitle}>
        HFS will use information below to verify your identity and communicate with you.
      </div>
      <div className={styles.form_row}>
        <Input
          label={InputLabel.BusinessOwnerFirstName}
          name="intermediary_main_contact_first_name"
          value={watcher.intermediary_main_contact_first_name}
          errorMessage={errors.intermediary_main_contact_first_name?.message}
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors('intermediary_main_contact_first_name');
            setValue('intermediary_main_contact_first_name', event.target.value);
          }}
        />
        <Input
          label={InputLabel.BusinessOwnerLastName}
          name="intermediary_main_contact_last_name"
          value={watcher.intermediary_main_contact_last_name}
          errorMessage={errors.intermediary_main_contact_last_name?.message}
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors('intermediary_main_contact_last_name');
            setValue('intermediary_main_contact_last_name', event.target.value);
          }}
        />
      </div>
      <div className={styles.form_row}>
        <PhoneNumberInput
          label={InputLabel.PrimaryContactPersonPhone}
          name="intermediary_main_contact_phone"
          value={watcher.intermediary_main_contact_phone}
          errorMessage={errors.intermediary_main_contact_phone?.message}
          className={styles.input}
          inputContainerClassname={styles.input}
          onChange={(value: string) => {
            clearErrors('intermediary_main_contact_phone');
            setValue('intermediary_main_contact_phone', value);
          }}
          onBlur={() => {
            if (watcher.intermediary_main_contact_phone) {
              trigger('intermediary_main_contact_phone');
            }
          }}
        />
        <Input
          label={InputLabel.PrimaryContactPersonEmail}
          name="intermediary_main_contact_email"
          value={watcher.intermediary_main_contact_email?.toLowerCase()}
          errorMessage={errors?.intermediary_main_contact_email?.message}
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors('intermediary_main_contact_email');
            setValue('intermediary_main_contact_email', event.target.value);
          }}
          onBlur={() => {
            if (watcher.intermediary_main_contact_email) {
              trigger('intermediary_main_contact_email');
            }
          }}
        />
      </div>
      <div className={styles.form_row}>
        <Select
          label={InputLabel.PrimaryContactPersonTitle}
          name="intermediary_main_contact_position"
          useFormControl
          control={control}
          required
          options={MAIN_CONTACT_POSITION_OPTIONS}
          value={MAIN_CONTACT_POSITION_OPTIONS.find(
            (option) => option.label === watcher.intermediary_main_contact_position,
          )}
          errorMessage={errors?.intermediary_main_contact_position?.message}
          inputContainerClassName={styles.input}
          className={styles.dropdown}
          isClearable={!!watcher.intermediary_main_contact_position}
        />
      </div>
    </>
  );
};

export default ContactPersonInformation;
